import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BLOG_POSTS } from '../mock';
import { ArrowRight, Calendar } from 'lucide-react';
import CTASection from '../components/CTASection';
import useSEO from '../hooks/useSEO';

const CATEGORIES = ['All', ...Array.from(new Set(BLOG_POSTS.map(p => p.category)))];

const Blog = () => {
  useSEO({
    title: 'Blog, Marketing Agency Liverpool | Yellow',
    description: 'Practical marketing advice from Yellow, a marketing agency Liverpool. How to bring back clients, keep staff and stop competing on price. Real cases, honest thinking.',
    path: '/blog'
  });
  const [filter, setFilter] = useState('All');
  const posts = filter === 'All' ? BLOG_POSTS : BLOG_POSTS.filter(p => p.category === filter);
  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <>
      <section className="pt-36 pb-14 lg:pt-44">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <p className="section-label mb-6">Blog & Insights</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.02] max-w-5xl">
            Advice for how to <span className="text-[#F5CD1B]">improve your communication with the world.</span>
          </h1>
          <p className="text-white/70 text-xl leading-relaxed mt-8 max-w-3xl">
            Practical, no-fluff insights on strategy, video, design, digital and the industries we work in.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-2 mb-10">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition-colors ${
                  filter === c
                    ? 'bg-[#F5CD1B] border-[#F5CD1B] text-[#0b0b0d]'
                    : 'border-white/10 text-white/70 hover:border-[#F5CD1B]/40 hover:text-[#F5CD1B]'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {featured && (
            <Link to={featured.path} className="grid lg:grid-cols-2 gap-6 mb-10 group cursor-pointer">
              <div className="rounded-2xl overflow-hidden aspect-[16/10] bg-[#111114] border border-white/10 relative">
                <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" onError={(e)=>{e.target.style.display='none'}} />
                <div className="absolute top-4 left-4">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#0b0b0d] bg-[#F5CD1B] px-3 py-1.5 rounded-md shadow-lg">
                    ★ Latest Blog
                  </span>
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[10px] uppercase tracking-widest text-[#F5CD1B] bg-[#F5CD1B]/10 border border-[#F5CD1B]/25 px-2 py-1 rounded-md">{featured.category}</span>
                  <span className="flex items-center gap-1 text-white/50 text-xs"><Calendar size={12} /> {featured.date}</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.1] group-hover:text-[#F5CD1B] transition-colors">
                  {featured.title}
                </h2>
                <p className="text-white/70 leading-relaxed mt-5 text-lg">{featured.excerpt}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-[#F5CD1B] font-semibold">Read article <ArrowRight size={16} /></div>
              </div>
            </Link>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rest.map((p) => (
              <Link key={p.id} to={p.path} className="card-hover group bg-[#111114] border border-white/10 rounded-2xl overflow-hidden cursor-pointer block">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" onError={(e)=>{e.target.style.display='none'}} />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[10px] uppercase tracking-widest text-[#F5CD1B]">{p.category}</span>
                    <span className="flex items-center gap-1 text-white/50 text-xs"><Calendar size={12} /> {p.date}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-white group-hover:text-[#F5CD1B] transition-colors leading-snug">{p.title}</h3>
                  <p className="text-white/60 text-sm mt-3 leading-relaxed">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Blog;
